import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import authFetch from '../utils/authFetch';
import { normalizeScoreBreakdown, scoreBarColorClass } from '../utils/scoreBreakdown';
import { useToast } from './ToastProvider';
import { 
    UsersIcon, 
    DocumentTextIcon, 
    CurrencyDollarIcon,
    ChartBarIcon,
    CogIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    MagnifyingGlassIcon,
    TrashIcon,
    PlusIcon,
    EnvelopeIcon,
    MegaphoneIcon,
    EyeIcon,
    ArrowPathIcon,
    PaperAirplaneIcon,
    ArrowRightOnRectangleIcon,
    CalendarDaysIcon,
    ClockIcon,
    NewspaperIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';

const AdminDashboard = () => {
    const toast = useToast();
    const [activeTab, setActiveTab] = useState('overview');
    const [isLoading, setIsLoading] = useState(true);
    const [stats, setStats] = useState({});
    const [users, setUsers] = useState([]);
    const [analyses, setAnalyses] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    

    // État guides SEO
    const [guides, setGuides] = useState([]);
    const [guidesStats, setGuidesStats] = useState({ totalTopics: 0, remaining: 0 });
    const [isGenerating, setIsGenerating] = useState(false);

    // État pour l'envoi d'email à toute la base
    const [emailSubject, setEmailSubject] = useState('');
    const [emailHtml, setEmailHtml] = useState('');
    const [emailRecipientCount, setEmailRecipientCount] = useState(null);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [showEmailPreview, setShowEmailPreview] = useState(false);

    const token = useSelector((state) => state.auth.token);
    const user = useSelector((state) => state.auth.user);
    const API_BASE = 'https://cvanalyzerback.osc-fr1.scalingo.io/api';

    // Fonction utilitaire pour les appels API avec gestion d'erreur
    const apiCall = async (url, options = {}) => {
        try {
            const response = await authFetch(url, options);

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`❌ Erreur API ${response.status}:`, errorText);
                throw new Error(`Erreur ${response.status}: ${errorText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('❌ Erreur lors de l\'appel API:', error);
            throw error;
        }
    };

    const fetchAdminData = useCallback(async () => {
        try {
            console.log('🔍 Début du chargement des données admin...');
            
            // Récupérer la liste des utilisateurs avec les paramètres requis
            console.log('👥 Récupération des utilisateurs...');
            try {
                const adminUsersResult = await apiCall(`${API_BASE}/admin/users?limit=1000`);
                const adminUsersData = adminUsersResult.users || [];
                console.log('👥 Nombre d\'utilisateurs récupérés via admin:', adminUsersData.length);
                setUsers(adminUsersData);
            } catch (error) {
                console.log('⚠️ Route admin users indisponible, fallback liste users...');
                try {
                    const usersData = await apiCall(`${API_BASE}/users?_start=0&_end=1000&_order=DESC&_sort=createdAt`);
                    console.log('👥 Nombre d\'utilisateurs récupérés:', usersData.length);
                    setUsers(usersData);
                } catch (adminError) {
                    console.log('❌ Aucune route utilisateurs disponible, utilisation de données vides');
                    setUsers([]);
                }
            }

            console.log('📄 Récupération des analyses...');
            try {
                const analysesResult = await apiCall(`${API_BASE}/admin/analyses?limit=50`);
                const analysesData = analysesResult.analyses || [];
                console.log('📄 Nombre d\'analyses récupérées:', analysesData.length);
                setAnalyses(analysesData);
            } catch (error) {
                console.log('⚠️ Route analyses admin non disponible, utilisation de données vides');
                setAnalyses([]);
            }

            // Récupérer les statistiques admin
            console.log('📊 Récupération des statistiques...');
            try {
                const statsData = await apiCall(`${API_BASE}/admin/stats`);
                console.log('📊 Statistiques récupérées:', statsData);
                setStats(statsData);
            } catch (error) {
                console.log('⚠️ Route stats admin non disponible, calcul local...');
                // Calculer les stats localement en fallback
                const currentStats = {
                    totalUsers: users.length, // Utiliser l'état local users
                    totalAnalyses: 0,
                    totalRevenue: 0,
                    activeUsers: 0,
                    userGrowth: 0,
                    analysisGrowth: 0,
                    revenueGrowth: 0,
                    activeGrowth: 0
                };
                console.log('📊 Stats calculées localement:', currentStats);
                setStats(currentStats);
            }

        } catch (error) {
            console.error('❌ Erreur lors du chargement des données admin:', error);
            toast.error(
                'Administration',
                'Erreur lors du chargement des données. Vérifiez votre session et réessayez.'
            );
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if (user?.username === "Guillaume") {
            fetchAdminData();
        }
    }, [user, fetchAdminData]);

    const fetchEmailRecipientCount = useCallback(async () => {
        try {
            const data = await apiCall(`${API_BASE}/admin/email/count`);
            setEmailRecipientCount(data.count ?? 0);
        } catch (e) {
            console.error('Erreur récupération nombre destinataires:', e);
            setEmailRecipientCount(0);
        }
    }, [token]);

    useEffect(() => {
        if (activeTab === 'email' && user?.username === 'Guillaume') {
            fetchEmailRecipientCount();
        }
        if (activeTab === 'guides') {
            fetchGuides();
        }
    }, [activeTab, user?.username, fetchEmailRecipientCount]);

    const fetchGuides = async () => {
        try {
            const data = await apiCall(`${API_BASE}/admin/guides`);
            setGuides(data.guides || []);
            setGuidesStats({ totalTopics: data.totalTopics, remaining: data.remaining });
        } catch {}
    };

    const handleToggleGuide = async (id) => {
        try {
            const data = await apiCall(`${API_BASE}/admin/guides/${id}/toggle`, { method: 'PATCH' });
            setGuides(prev => prev.map(g => g.id === id ? { ...g, isPublished: data.isPublished } : g));
        } catch {}
    };

    const handleDeleteGuide = async (id, title) => {
        if (!window.confirm(`Supprimer "${title}" ?`)) return;
        try {
            await apiCall(`${API_BASE}/admin/guides/${id}`, { method: 'DELETE' });
            setGuides(prev => prev.filter(g => g.id !== id));
        } catch {}
    };

    const handleGenerateGuides = async () => {
        setIsGenerating(true);
        try {
            const result = await apiCall(`${API_BASE}/admin/guides/generate`, {
                method: 'POST',
                body: JSON.stringify({ max: 5 }),
            });
            toast.success(
                'Guides SEO',
                `${result.generated} guide(s) généré(s). Restants : ${result.remaining}`
            );
            fetchGuides();
        } catch (err) {
            toast.error('Génération', err.message || 'Erreur lors de la génération des guides.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSendEmailToAll = async () => {
        if (!emailSubject.trim() || !emailHtml.trim()) {
            toast.warning('Email', 'Veuillez remplir le sujet et le contenu de l’email.');
            return;
        }
        const count = emailRecipientCount ?? 0;
        if (count === 0) {
            toast.warning('Email', 'Aucun destinataire dans la base.');
            return;
        }
        if (!window.confirm(`Envoyer cet email à ${count} utilisateur(s) ? Cette action est irréversible.`)) {
            return;
        }
        setIsSendingEmail(true);
        try {
            const result = await apiCall(`${API_BASE}/admin/email/send`, {
                method: 'POST',
                body: JSON.stringify({ subject: emailSubject.trim(), htmlContent: emailHtml.trim() })
            });
            toast.success(
                'Envoi terminé',
                `${result.sent}/${result.total} email(s) envoyé(s).`
            );
            setEmailSubject('');
            setEmailHtml('');
            setShowEmailPreview(false);
        } catch (err) {
            console.error(err);
            toast.error('Envoi', err.message || "Erreur lors de l'envoi des emails.");
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleAddCredits = async (userId, credits) => {
        try {
            console.log('💰 Ajout de crédits pour l\'utilisateur:', userId, 'Montant:', credits);
            
            const result = await apiCall(`https://cvanalyzerback.osc-fr1.scalingo.io/api/admin/users/${userId}/credits`, {
                method: 'POST',
                body: JSON.stringify({ 
                    action: 'add',
                    credits: credits 
                })
            });

            console.log('✅ Crédits ajoutés avec succès:', result);
            fetchAdminData(); // Recharger les données
        } catch (error) {
            console.error('❌ Erreur lors de l\'ajout de crédits:', error);
            toast.error('Crédits', error.message || "Erreur lors de l'ajout de crédits.");
        }
    };

    const handleSetCredits = async (userId, credits) => {
        try {
            console.log('💰 Définition de crédits pour l\'utilisateur:', userId, 'Montant:', credits);
            
            const result = await apiCall(`https://cvanalyzerback.osc-fr1.scalingo.io/api/admin/users/${userId}/credits`, {
                method: 'POST',
                body: JSON.stringify({ 
                    action: 'set',
                    credits: credits 
                })
            });

            console.log('✅ Crédits définis avec succès:', result);
            fetchAdminData(); // Recharger les données
        } catch (error) {
            console.error('❌ Erreur lors de la définition de crédits:', error);
            toast.error('Crédits', error.message || 'Erreur lors de la définition des crédits.');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
            return;
        }

        try {
            const result = await apiCall(`https://cvanalyzerback.osc-fr1.scalingo.io/api/admin/users/${userId}`, {
                method: 'DELETE'
            });

            console.log('✅ Utilisateur supprimé avec succès:', result);
            fetchAdminData(); // Recharger les données
        } catch (error) {
            console.error('❌ Erreur lors de la suppression:', error);
            toast.error('Suppression', error.message || 'Erreur lors de la suppression de l’utilisateur.');
        }
    };

    const filteredUsers = users.filter(user => 
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );


    const getGrowthIcon = (value) => {
        if (value > 0) return <ArrowTrendingUpIcon className="w-5 h-5 text-green-500" />;
        if (value < 0) return <ArrowTrendingDownIcon className="w-5 h-5 text-red-500" />;
        return <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full"></div>;
    };

    const formatMom = (count, growth) => {
        const signed = growth > 0 ? `+${growth}` : `${growth}`;
        return `${count ?? 0} ce mois · ${signed}% vs mois dernier`;
    };

    if (user?.username !== "Guillaume") {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
                <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                    <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Accès refusé</h2>
                    <p className="text-gray-600">Vous n'avez pas les permissions pour accéder à cette page.</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement du tableau de bord admin...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pt-16 p-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2">Tableau de bord Admin</h1>
                    <p className="text-gray-600">Gestion complète de la plateforme CV Analyzer</p>
                </div>

                {/* Navigation Tabs */}
                <div className="bg-white rounded-2xl shadow-xl p-2 mb-8">
                    <div className="flex space-x-1">
                        {[
                            { id: 'overview', label: 'Vue d\'ensemble', icon: ChartBarIcon },
                            { id: 'users', label: 'Utilisateurs', icon: UsersIcon },
                            { id: 'analyses', label: 'Analyses', icon: DocumentTextIcon },
                            { id: 'email', label: 'Email base', icon: MegaphoneIcon },
                            { id: 'guides', label: 'Guides SEO', icon: NewspaperIcon },
                            { id: 'settings', label: 'Paramètres', icon: CogIcon }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                                    activeTab === tab.id
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                <tab.icon className="w-5 h-5 mr-2" />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-8">
                        {/* Key Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Utilisateurs totaux</p>
                                        <p className="text-3xl font-bold text-blue-600">{stats.totalUsers || 0}</p>
                                        <div className="flex items-center mt-2">
                                            {getGrowthIcon(stats.userGrowth || 0)}
                                            <span className="text-sm text-gray-600 ml-1">{formatMom(stats.usersThisMonth, stats.userGrowth)}</span>
                                        </div>
                                    </div>
                                    <UsersIcon className="w-8 h-8 text-blue-500" />
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Analyses totales</p>
                                        <p className="text-3xl font-bold text-green-600">{stats.totalAnalyses || 0}</p>
                                        <div className="flex items-center mt-2">
                                            {getGrowthIcon(stats.analysisGrowth || 0)}
                                            <span className="text-sm text-gray-600 ml-1">{formatMom(stats.analysesThisMonth ?? stats.analyses?.thisMonth, stats.analysisGrowth)}</span>
                                        </div>
                                    </div>
                                    <DocumentTextIcon className="w-8 h-8 text-green-500" />
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Revenus</p>
                                        <p className="text-3xl font-bold text-purple-600">{stats.totalRevenue || 0}€</p>
                                        <div className="flex items-center mt-2">
                                            {getGrowthIcon(stats.revenueGrowth || 0)}
                                            <span className="text-sm text-gray-600 ml-1">{stats.revenueGrowth || 0}% ce mois</span>
                                        </div>
                                    </div>
                                    <CurrencyDollarIcon className="w-8 h-8 text-purple-500" />
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Connectés ce mois</p>
                                        <p className="text-3xl font-bold text-orange-600">{stats.activeUsers || 0}</p>
                                        <div className="flex items-center mt-2">
                                            {getGrowthIcon(stats.activeGrowth || 0)}
                                            <span className="text-sm text-gray-600 ml-1">
                                                {stats.activeGrowth > 0 ? '+' : ''}{stats.activeGrowth || 0}% vs mois dernier
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">{stats.analyzedThisMonth ?? 0} ont analysé ce mois</p>
                                    </div>
                                    <CheckCircleIcon className="w-8 h-8 text-orange-500" />
                                </div>
                            </div>
                        </div>

                        {/* KPIs secondaires */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Analyses aujourd&apos;hui</p>
                                        <p className="text-2xl font-bold text-teal-600">{stats.analyses?.today ?? 0}</p>
                                    </div>
                                    <CalendarDaysIcon className="w-8 h-8 text-teal-500" />
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Analyses (7 derniers jours)</p>
                                        <p className="text-2xl font-bold text-cyan-600">{stats.analyses?.thisWeek ?? 0}</p>
                                    </div>
                                    <DocumentTextIcon className="w-8 h-8 text-cyan-500" />
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Score moyen (CV)</p>
                                        <p className="text-2xl font-bold text-amber-600">{stats.analyses?.averageScore ?? '—'}/100</p>
                                    </div>
                                    <ChartBarIcon className="w-8 h-8 text-amber-500" />
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Taux de conversion</p>
                                        <p className="text-2xl font-bold text-indigo-600">{stats.conversionRate ?? 0}%</p>
                                        <p className="text-xs text-gray-500 mt-1">{stats.convertedUsers ?? 0} / {stats.totalUsers || 0} ont analysé</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Connexions aujourd&apos;hui</p>
                                        <p className="text-2xl font-bold text-emerald-600">{stats.loginsToday ?? 0}</p>
                                    </div>
                                    <ArrowRightOnRectangleIcon className="w-8 h-8 text-emerald-500" />
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Inactifs (&gt; 30 jours)</p>
                                        <p className="text-2xl font-bold text-slate-600">{stats.inactiveUsers30d ?? 0}</p>
                                        <p className="text-xs text-gray-500 mt-1">Pas connectés depuis 30 j</p>
                                    </div>
                                    <ClockIcon className="w-8 h-8 text-slate-500" />
                                </div>
                            </div>
                        </div>

                        {/* Top secteurs */}
                        {stats.topSectors?.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">Top secteurs d&apos;analyse</h3>
                                <div className="flex flex-wrap gap-3">
                                    {stats.topSectors.map((sector, index) => (
                                        <span
                                            key={sector.name || index}
                                            className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-50 text-blue-800"
                                        >
                                            {sector.name || 'Non renseigné'}
                                            <span className="ml-2 text-blue-600 font-semibold">{sector.count}</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Recent Activity */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-6">Activité récente</h3>
                                <div className="space-y-4">
                                    {analyses.slice(0, 5).map((analysis, index) => (
                                        <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-800 text-sm">
                                                    {analysis.user?.username || 'Utilisateur'}
                                                </p>
                                                <p className="text-xs text-gray-600">{analysis.sector} - {analysis.score}/100</p>
                                            </div>
                                            <span className="text-xs text-gray-500">
                                                {new Date(analysis.createdAt).toLocaleDateString('fr-FR')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-6">Utilisateurs récents</h3>
                                <div className="space-y-4">
                                    {users.slice(0, 5).map((user, index) => (
                                        <div key={user.id || index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-800 text-sm">{user.username}</p>
                                                <p className="text-xs text-gray-600">{user.email}</p>
                                            </div>
                                            <span className="text-xs text-gray-500">
                                                {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                                    <ArrowRightOnRectangleIcon className="w-5 h-5 text-green-600" />
                                    Dernières connexions
                                </h3>
                                <div className="space-y-4">
                                    {[...users]
                                        .sort((a, b) => {
                                            const da = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0;
                                            const db = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0;
                                            return db - da;
                                        })
                                        .slice(0, 5)
                                        .map((user, index) => (
                                            <div key={user.id || index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                                                <div className="flex-1">
                                                    <p className="font-medium text-gray-800 text-sm">{user.username}</p>
                                                    <p className="text-xs text-gray-600">{user.email}</p>
                                                </div>
                                                <span className="text-xs text-gray-500">
                                                    {user.lastLoginAt
                                                        ? new Date(user.lastLoginAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                                                        : 'Jamais connecté'}
                                                </span>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-800">Gestion des utilisateurs</h2>
                            <div className="flex items-center space-x-4">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Rechercher un utilisateur..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left py-3 px-4 font-semibold text-gray-800">Utilisateur</th>
                                        <th className="text-left py-3 px-4 font-semibold text-gray-800">Email</th>
                                        <th className="text-left py-3 px-4 font-semibold text-gray-800">Crédits</th>
                                        <th className="text-left py-3 px-4 font-semibold text-gray-800">Analyses</th>
                                        <th className="text-left py-3 px-4 font-semibold text-gray-800">Inscription</th>
                                        <th className="text-left py-3 px-4 font-semibold text-gray-800">Dernière connexion</th>
                                        <th className="text-left py-3 px-4 font-semibold text-gray-800">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map((user) => (
                                        <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="py-3 px-4">
                                                <div>
                                                    <p className="font-medium text-gray-800">{user.username}</p>
                                                    <p className="text-sm text-gray-600">ID: {user.id}</p>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-gray-600">{user.email}</td>
                                            <td className="py-3 px-4">
                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                                    {parseFloat(user.credits) || 0}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-gray-600">{user.analysisCount ?? user.analysesCount ?? 0}</td>
                                            <td className="py-3 px-4 text-gray-600">
                                                {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                                            </td>
                                            <td className="py-3 px-4 text-gray-600">
                                                {user.lastLoginAt
                                                    ? new Date(user.lastLoginAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                                                    : '—'}
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => handleAddCredits(user.id, 10)}
                                                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                        title="Ajouter 10 crédits"
                                                    >
                                                        <PlusIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleSetCredits(user.id, 50)}
                                                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                                                        title="Définir 50 crédits"
                                                    >
                                                        <span className="text-xs font-bold">50</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteUser(user.id)}
                                                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                        title="Supprimer l'utilisateur"
                                                    >
                                                        <TrashIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Analyses Tab */}
                {activeTab === 'analyses' && (
                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">Analyses récentes</h2>
                        <div className="space-y-3">
                            {analyses.map((analysis, index) => {
                                const bd = normalizeScoreBreakdown(analysis.scoreBreakdown, analysis.score);
                                const keys = bd
                                    ? Object.entries(bd).filter(([, val]) => typeof val === 'number')
                                    : [];
                                const scoreColor = analysis.score >= 70 ? 'bg-green-100 text-green-800' : analysis.score >= 40 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800';
                                return (
                                    <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                        <div className="flex items-center justify-between mb-2">
                                            <div>
                                                <p className="font-medium text-gray-800">{analysis.user?.username || 'Utilisateur'}</p>
                                                <p className="text-xs text-gray-500">{analysis.sector} · {analysis.filename}</p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`px-2 py-1 rounded-full text-sm font-bold ${scoreColor}`}>
                                                    {analysis.score}/100
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    {new Date(analysis.createdAt).toLocaleDateString('fr-FR')}
                                                </span>
                                            </div>
                                        </div>
                                        {keys.length > 0 && (
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                                                {keys.map(([key, val]) => (
                                                    <div key={key}>
                                                        <div className="flex justify-between text-xs text-gray-500 mb-0.5">
                                                            <span className="capitalize">{key}</span>
                                                            <span className="font-medium">{val}/100</span>
                                                        </div>
                                                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                            <div
                                                                className={`h-1.5 rounded-full ${scoreBarColorClass(val)}`}
                                                                style={{ width: `${Math.min(100, Math.max(0, val))}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {!bd && (
                                            <p className="text-xs text-gray-400 mt-2 italic">Détail non disponible (analyse ancienne)</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Email base Tab */}
                {activeTab === 'email' && (
                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Envoyer un email à toute la base</h2>
                        <p className="text-gray-600 mb-6">Composez un email qui sera envoyé à tous les utilisateurs inscrits. Utilisez <code className="bg-gray-100 px-1 rounded">&#123;&#123;username&#125;&#125;</code> pour personnaliser avec le prénom/nom.</p>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Sujet</label>
                                <input
                                    type="text"
                                    value={emailSubject}
                                    onChange={(e) => setEmailSubject(e.target.value)}
                                    placeholder="Ex. Nouveautés sur l'analyse de CV"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contenu (HTML)</label>
                                <textarea
                                    value={emailHtml}
                                    onChange={(e) => setEmailHtml(e.target.value)}
                                    placeholder="<p>Bonjour {{username}},</p><p>...</p>"
                                    rows={10}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-3 mb-6">
                            <button
                                type="button"
                                onClick={() => setShowEmailPreview(!showEmailPreview)}
                                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                            >
                                <EyeIcon className="w-5 h-5 mr-2" />
                                {showEmailPreview ? 'Masquer l\'aperçu' : 'Prévisualiser'}
                            </button>
                            {emailRecipientCount !== null && (
                                <span className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-800 rounded-lg">
                                    Sera envoyé à <strong className="mx-1">{emailRecipientCount}</strong> utilisateur(s)
                                </span>
                            )}
                        </div>

                        {showEmailPreview && (emailSubject || emailHtml) && (
                            <div className="mb-6 p-4 border border-gray-200 rounded-xl bg-gray-50">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2">Aperçu (exemple avec username &quot;Jean&quot; — un lien vers la plateforme est ajouté automatiquement en bas)</h3>
                                <div className="bg-white rounded-lg p-4 shadow-inner border border-gray-100 min-h-[120px]">
                                    <p className="text-xs text-gray-500 mb-1 font-semibold">{emailSubject || '(sans sujet)'}</p>
                                    <div
                                        className="prose prose-sm max-w-none"
                                        dangerouslySetInnerHTML={{
                                            __html: (emailHtml || '')
                                                .replace(/\{\{username\}\}/g, 'Jean')
                                                .replace(/\{\{email\}\}/g, 'jean@exemple.fr')
                                                + (() => {
                                                    const baseUrl = (process.env.REACT_APP_CLIENT_URL || 'https://www.cv-analyse.fr').replace(/\/$/, '');
                                                    const displayUrl = baseUrl.replace(/^https?:\/\//, '');
                                                    return `<div style="margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280;"><p>Retrouvez-nous sur <a href="${baseUrl}" style="color: #2563eb;">${displayUrl}</a></p></div>`;
                                                })()
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={handleSendEmailToAll}
                            disabled={isSendingEmail || !emailSubject.trim() || !emailHtml.trim()}
                            className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                            {isSendingEmail ? (
                                <>
                                    <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                                    Envoi en cours...
                                </>
                            ) : (
                                <>
                                    <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                                    Envoyer à toute la base
                                </>
                            )}
                        </button>
                    </div>
                )}

                {/* Guides SEO Tab */}
                {activeTab === 'guides' && (
                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">Guides SEO</h2>
                                <p className="text-gray-500 text-sm mt-1">
                                    {guides.length}/{guidesStats.totalTopics} générés · {guidesStats.remaining} restants
                                </p>
                            </div>
                            <button
                                onClick={handleGenerateGuides}
                                disabled={isGenerating || guidesStats.remaining === 0}
                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isGenerating ? (
                                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                ) : (
                                    <SparklesIcon className="w-4 h-4" />
                                )}
                                {isGenerating ? 'Génération...' : 'Générer 5 guides'}
                            </button>
                        </div>

                        {/* Barre de progression */}
                        <div className="mb-6">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>Progression</span>
                                <span>{Math.round((guides.length / guidesStats.totalTopics) * 100)}%</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                                <div
                                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all"
                                    style={{ width: `${(guides.length / guidesStats.totalTopics) * 100}%` }}
                                />
                            </div>
                        </div>

                        {/* Liste des guides */}
                        <div className="space-y-2">
                            {guides.length === 0 ? (
                                <p className="text-gray-400 text-center py-8">Aucun guide généré. Cliquez sur "Générer 5 guides" pour commencer.</p>
                            ) : (
                                guides.map(guide => (
                                    <div key={guide.id} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <span className={`w-2 h-2 rounded-full shrink-0 ${guide.isPublished ? 'bg-green-500' : 'bg-gray-300'}`} />
                                            <div className="min-w-0">
                                                <p className="font-medium text-gray-800 text-sm truncate">{guide.metaTitle}</p>
                                                <p className="text-xs text-gray-400">/guide/{guide.slug} · {guide.category}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 ml-3">
                                            <a
                                                href={`https://www.cv-analyse.fr/guide/${guide.slug}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="p-1.5 text-blue-500 hover:text-blue-700 transition-colors"
                                                title="Voir"
                                            >
                                                <EyeIcon className="w-4 h-4" />
                                            </a>
                                            <button
                                                onClick={() => handleToggleGuide(guide.id)}
                                                className={`text-xs px-2 py-1 rounded-lg font-medium transition-colors ${guide.isPublished ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                            >
                                                {guide.isPublished ? 'Publié' : 'Masqué'}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteGuide(guide.id, guide.metaTitle)}
                                                className="p-1.5 text-red-400 hover:text-red-600 transition-colors"
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <p className="text-xs text-gray-400 mt-4 text-center">
                            Le cron génère automatiquement 5 nouveaux guides chaque lundi à 3h.
                        </p>
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">Paramètres système</h2>
                        <div className="space-y-6">
                            <div className="p-4 bg-blue-50 rounded-lg">
                                <h3 className="font-semibold text-blue-800 mb-2">Configuration générale</h3>
                                <p className="text-blue-700 text-sm">Paramètres de la plateforme et configuration système.</p>
                            </div>
                            <div className="p-4 bg-green-50 rounded-lg">
                                <h3 className="font-semibold text-green-800 mb-2">Gestion des crédits</h3>
                                <p className="text-green-700 text-sm">Configuration des prix et des packages de crédits.</p>
                            </div>
                            <div className="p-4 bg-purple-50 rounded-lg">
                                <h3 className="font-semibold text-purple-800 mb-2">Sécurité</h3>
                                <p className="text-purple-700 text-sm">Paramètres de sécurité et permissions utilisateurs.</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard; 